import { isPrimitive, isPrimitiveArray } from '@smartsheet-dataflow/formjs';
import isNil from 'lodash/fp/isNil';
import {
  AllParamModel,
  GroupParamModel,
  HtmlLinkParamModel,
  ParamModel,
} from './params/ParamModelTypes';
import {
  ParamSchema,
  ParamSchemaTypes,
  PrimitiveParamSchemaTypes,
} from './params/ParamSchemaTypes';

export const getParameterId = (
  param: ParamSchema | string,
  parentId?: string
) => {
  const paramId = typeof param === 'string' ? param : param.param;
  return parentId ? `${parentId}.${paramId}` : paramId;
};

export const getParameterType = (param: ParamSchema) => {
  switch (param.type) {
    case 'ENUM':
      return param.allowArray ? 'ENUM_ARRAY' : 'ENUM';
    case 'PARAM':
      return param.allowArray ? 'GROUP_ARRAY' : 'GROUP';
    case 'TEXT':
    case 'JSON':
    case 'DERIVED-JSON':
    case 'DERIVED':
      return 'TEXT';
    case 'STRING':
      return param.allowArray ? 'STRING_ARRAY' : 'STRING';
    default:
      return param.type.toUpperCase();
  }
};

export const getDerivedType = (type: string) => {
  switch (type) {
    case 'GROUP':
    case 'GROUP_ARRAY':
      return 'PARAM';
    case 'STRING_ARRAY':
      return 'STRING';
    default:
      return type.toUpperCase();
  }
};

export const isPrimitiveParamType = (
  type: string
): type is PrimitiveParamSchemaTypes => {
  return (
    type === ParamSchemaTypes.TEXT ||
    type === ParamSchemaTypes.PASSWORD ||
    type === ParamSchemaTypes.STRING ||
    type === ParamSchemaTypes.NUMBER ||
    type === ParamSchemaTypes.HTML ||
    type === ParamSchemaTypes.JSON ||
    type === ParamSchemaTypes.ENUM ||
    type === ParamSchemaTypes['BRIDGE.WORKFLOW'] ||
    type === ParamSchemaTypes['DERIVED-JSON'] ||
    type === ParamSchemaTypes.DERIVED ||
    type === ParamSchemaTypes.BOOLEAN
  );
};

export const isMapParamType = (
  type: string
): type is PrimitiveParamSchemaTypes => {
  return type === ParamSchemaTypes.MAP;
};

export const isGroupParamType = (
  type: string
): type is PrimitiveParamSchemaTypes => {
  return type === ParamSchemaTypes.PARAM;
};

export const isHTMLLinkType = (
  type: string
): type is PrimitiveParamSchemaTypes => {
  return type === ParamSchemaTypes['HTML.LINK'];
};

export const isParamModelType = <T extends ParamModel>(
  param: ParamModel
): param is T => {
  return (
    param.value !== undefined &&
    param.type !== undefined &&
    param.isArray !== undefined
  );
};

export const isGroupModel = (
  model: AllParamModel | undefined
): model is GroupParamModel =>
  !isNil(model) && !isPrimitive(model) && !Array.isArray(model);

export const isHTMLLinkModel = (
  model: AllParamModel | undefined
): model is HtmlLinkParamModel =>
  !isNil(model) &&
  !isPrimitive(model) &&
  !Array.isArray(model) &&
  model.value !== undefined &&
  model.label !== undefined &&
  Object.keys(model).length === 2;

export const isGroupArrayModel = (
  model: AllParamModel | undefined
): model is GroupParamModel[] =>
  !isNil(model) && !isPrimitiveArray(model) && Array.isArray(model);
